/**
 * Tiny builder for the RSQL query strings the backend's
 * `RsqlToSpecification` expects on `?filter=`. Kept as pure functions so the
 * quoting/combining rules are unit-testable without a live parser.
 *
 * Grammar notes (mirrors cz.jirutka rsql-parser, the library the backend
 * actually parses with):
 *   - `field==value` (a bare `*` inside an EQUAL value is treated as a LIKE
 *     wildcard by the backend visitor — see RsqlToSpecification.Visitor)
 *   - `field=in=(v1,v2,...)`
 *   - `;` = AND, `,` = OR; string literals are double-quoted so values with
 *     spaces (e.g. "United Kingdom") parse correctly.
 */

function quote(value: string): string {
  const escaped = value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  return `"${escaped}"`
}

export function rsqlEquals(field: string, value: string): string {
  return `${field}==${quote(value)}`
}

/** `field` contains `term` anywhere, case-insensitively (backend LOWER()s both sides). */
export function rsqlContains(field: string, term: string): string {
  return `${field}==${quote(`*${term}*`)}`
}

export function rsqlIn(field: string, values: readonly string[]): string | null {
  if (values.length === 0) return null
  if (values.length === 1) return rsqlEquals(field, values[0]!)
  return `${field}=in=(${values.map(quote).join(',')})`
}

function wrap(clauses: readonly string[]): string {
  return clauses.length > 1 ? `(${clauses.join(',')})` : clauses[0]!
}

/** AND-combines every non-empty clause. */
export function rsqlAnd(...clauses: Array<string | null | undefined | false>): string {
  const parts = clauses.filter((c): c is string => Boolean(c))
  return parts.join(';')
}

/** OR-combines every non-empty clause (each candidate parenthesized if there is more than one). */
export function rsqlOr(...clauses: Array<string | null | undefined | false>): string | null {
  const parts = clauses.filter((c): c is string => Boolean(c))
  if (parts.length === 0) return null
  return wrap(parts)
}

/** Free-text search across a fixed set of fields, OR-combined with wildcard contains. */
export function rsqlSearch(fields: readonly string[], term: string): string | null {
  const trimmed = term.trim()
  if (!trimmed) return null
  return rsqlOr(...fields.map((f) => rsqlContains(f, trimmed)))
}
